import { io, Socket } from "socket.io-client";
import type { RemoteSession } from "./call.service/call.api";
import type { Conversation, ConversationMessage, MessageStatus } from "./conversation.service/conversation.api";

const BASE_URL = import.meta.env.VITE_API_URL as string;

// ─── Event Payloads ───────────────────────────────────────────────────────────

export interface ConversationMessageEvent {
  conversationId: string;
  message: ConversationMessage;
}

export interface ConversationUpdatedEvent {
  conversationId: string;
  lastMessagePreview?: string;
  lastMessageAt?: string;
  linkedTicketId?: string;
  unreadCount?: number;
}

export interface MessageStatusEvent {
  wamid: string;
  status: MessageStatus;
  errorReason?: string;
  errorCode?: number;
}

export interface ConversationReactionEvent {
  conversationId: string;
  wamid: string;
  reactions: Array<{ emoji: string; fromWaId: string }>;
}

/** Emitido quando um agente marca a conversa como lida — a caixa é compartilhada, então quem mais
 * estiver com a mesma conversa aberta precisa ver "visto por" atualizar sem reabrir/dar F5. */
export interface ConversationReadEvent {
  conversationId: string;
  readBy: Conversation["readBy"];
}

// ─── Acesso remoto (MeshCentral) — sempre dentro de uma chamada ativa ──────────

export interface RemoteSessionUpdateEvent {
  callId: string;
  session: RemoteSession;
}

// ─── Videochamada ───────────────────────────────────────────────────────────────
// Sala `call:<callId>` — só status (call:updated) e chat de texto; a mídia (áudio/vídeo) vai
// pelo LiveKit, não por aqui (ver useCallRoomSocket/useLiveKitToken).

/** Mensagem de texto ou link de acesso remoto postado no chat da chamada. */
export interface CallChatMessageEvent {
  kind: "text" | "remote-access-link";
  text?: string;
  url?: string;
  sessionId?: string;
  from?: "agent" | "client";
  at: string;
}

/** Mudança de status da chamada (atendida/recusada/encerrada) — emitido tanto pro agente quanto
 * pro cliente que já entraram na sala, pra que o lado que não iniciou a ação saiba que acabou. */
export interface CallUpdatedEvent {
  call: { _id: string; status: string; [key: string]: unknown };
}

type RealtimeEvents = {
  "conversation:message": ConversationMessageEvent;
  "conversation:updated": ConversationUpdatedEvent;
  "message:status": MessageStatusEvent;
  "conversation:reaction": ConversationReactionEvent;
  "conversation:read": ConversationReadEvent;
  "remote-session:update": RemoteSessionUpdateEvent;
  "call:chat-message": CallChatMessageEvent;
  "call:updated": CallUpdatedEvent;
};

type EventHandler<K extends keyof RealtimeEvents> = (payload: RealtimeEvents[K]) => void;

// ─── Singleton ────────────────────────────────────────────────────────────────

let socket: Socket | null = null;
// Assinaturas feitas antes do socket existir (ex.: um hook cujo efeito roda antes do efeito do
// componente irmão que chama connect()) — sem isso o listener nunca é anexado, o subscribe()
// falha silenciosamente (só um warning) e o evento simplesmente nunca chega no handler.
let pendingSubscriptions: Array<{ event: string; handler: (...args: unknown[]) => void }> = [];

function getSocket(): Socket | null {
  return socket;
}

/**
 * Conecta o socket do `RealtimeGateway`. Aceita tanto o JWT do agente (`auth_token`, entra na
 * sala `org:<organizationId>`) quanto o `client-jwt` do cliente final (`client_token`, usado
 * pelo cliente só pra entrar na sala `call:<callId>` da chamada pra qual foi convidado).
 */
function connect(token: string): Socket {
  // Strip "Bearer " prefix if present — socket auth expects raw JWT
  const rawToken = token.startsWith("Bearer ") ? token.slice(7) : token;

  // Reaproveita o socket já existente mesmo que o handshake ainda não tenha terminado — nunca
  // derruba e recria só porque `.connected` ainda é false. Isso quebrava especificamente sob
  // Strict Mode do React (dev): o efeito que chama connect() roda 2x, e derrubar a primeira
  // tentativa no meio do handshake pra criar uma segunda trava pra sempre em "conectando" (as duas
  // instâncias de socket.io-client compartilham o mesmo Manager/engine por padrão quando apontam
  // pra mesma URL+opções — desconectar a primeira corrompe a segunda). Emits ficam enfileirados
  // pelo próprio socket.io-client até a conexão de verdade completar, então é seguro devolver aqui.
  if (socket) {
    return socket;
  }

  socket = io(BASE_URL, {
    auth: { token: rawToken },
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    timeout: 20000,
  });

  for (const { event, handler } of pendingSubscriptions) {
    socket.on(event, handler);
  }

  socket.on("connect", () => {
    console.debug("[realtime] connected", socket?.id);
  });

  socket.on("disconnect", (reason) => {
    console.debug("[realtime] disconnected", reason);
  });

  socket.on("connect_error", (err) => {
    console.warn("[realtime] connect error", err.message);
  });

  return socket;
}

function disconnect(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

function subscribe<K extends keyof RealtimeEvents>(
  event: K,
  handler: EventHandler<K>
): () => void {
  // socket.io types are loose — cast to avoid overload issues
  const key = event as string;
  const fn = handler as (...args: unknown[]) => void;
  const s = getSocket();

  if (!s) {
    pendingSubscriptions.push({ event: key, handler: fn });
    return () => {
      pendingSubscriptions = pendingSubscriptions.filter(
        (p) => !(p.event === key && p.handler === fn)
      );
    };
  }

  s.on(key, fn);
  return () => {
    pendingSubscriptions = pendingSubscriptions.filter(
      (p) => !(p.event === key && p.handler === fn)
    );
    s.off(key, fn);
  };
}

export const realtimeService = {
  connect,
  disconnect,
  subscribe,
  getSocket,
};
